import React from 'react';

import { produce } from 'immer';
import { useAtomValue, useSetAtom } from 'jotai';
import { values } from 'lodash-es';

import { analyticsService } from '@/app/analytics';
import { atomPersist } from '@/utils/atom-persist';

import type { DecodeToken } from './userAuthContext';

type UserAuthContextState = {
  userID: number;
  accessToken: string;
  refreshToken: string;
  decodeToken: DecodeToken;
  roles: string[];
};

type UserAuthsStore = {
  /**
   * 현재 선택된 유저.
   */
  userID: number;

  /**
   * 사용자 인증 정보.
   */
  auths: Record<number, UserAuthContextState>;
};

const userAuthsAtom = atomPersist<UserAuthsStore>('user-auths-store', {
  userID: 0,
  auths: {},
});

/**
 * 데모 페이지에서 사용할 인증 스토어.
 */
const demoAuthsAtom = atomPersist<UserAuthsStore>('demo-auths-store', {
  userID: 0,
  auths: {},
});

const UserAuthAtomContext = React.createContext(userAuthsAtom);

/**
 * 사용자 인증 정보 설정 훅
 *
 * 사용자의 로그인 정보를 설정하고 관리합니다.
 * - 액세스 토큰, 리프레시 토큰 저장
 * - 사용자 권한 정보 관리
 * - 여러 사용자 계정 지원 (멀티 계정)
 * - 분석 서비스 사용자 정보 설정
 *
 * @returns 사용자 인증 정보 설정 함수
 */
export const useSetUserAuth = () => {
  const authAtom = React.useContext(UserAuthAtomContext);
  const setAuth = useSetAtom(authAtom);

  return React.useCallback<{
    (state: UserAuthContextState, parma1?: never): void;
    (userID: number, reducer: (state: UserAuthContextState) => UserAuthContextState): void;
  }>(
    (param0, param1) => {
      setAuth(prev => {
        const userID = typeof param0 === 'number' ? param0 : param0.userID;

        analyticsService.setUser(userID);

        return produce(prev, draft => {
          if (typeof param0 !== 'number') {
            draft.auths[userID] = param0;
            draft.userID = userID;
          } else {
            const found = draft.auths[userID];
            if (found && param1) {
              draft.auths[userID] = param1(found);
            }
          }
        });
      });
    },
    [setAuth],
  );
};

/**
 * 사용자 인증 정보를 로그아웃 시킵니다.
 */
export const useLogoutUserAuth = () => {
  const authAtom = React.useContext(UserAuthAtomContext);
  const setAuth = useSetAtom(authAtom);

  return React.useCallback(
    (userID?: number) => {
      setAuth(prev => {
        const target = userID ?? prev.userID;

        return produce(prev, draft => {
          if (draft.auths[target]) {
            delete draft.auths[target];
          }
          draft.userID = values(draft.auths)[0]?.userID ?? 0;
        });
      });

      analyticsService.setUser(undefined);
    },
    [setAuth],
  );
};

/**
 * 일반 사용자 인증 스토어를 제공합니다.
 */
export const UserAuthAtomProvider = ({ children }: { children: React.ReactNode }) => {
  return <UserAuthAtomContext.Provider value={userAuthsAtom}>{children}</UserAuthAtomContext.Provider>;
};

/**
 * 데모 사용자 인증 스토어를 제공합니다.
 */
export const DemoAuthAtomProvider = ({ children }: { children: React.ReactNode }) => {
  return <UserAuthAtomContext.Provider value={demoAuthsAtom}>{children}</UserAuthAtomContext.Provider>;
};

/**
 * 사용자 인증 정보 조회 훅
 *
 * 현재 로그인된 사용자의 인증 정보를 가져옵니다.
 * - 액세스 토큰, 리프레시 토큰
 * - 사용자 ID 및 디코드된 토큰 정보
 * - 사용자 권한(roles) 정보
 * - 쿠키 기반 영속 저장
 *
 * @returns 현재 사용자의 인증 정보
 */
export const useUserAuth = () => {
  const authAtom = React.useContext(UserAuthAtomContext);
  const value = useAtomValue(authAtom);
  return value.auths[value.userID];
};
